import { useState } from 'react';
import Modal from './Modal';
import HolidayForm from './HolidayForm';
import useDialog from '../hooks/useDialog';
import { useTranslation } from '../i18n';
import './HolidayModal.scss';

/**
 * Modal for managing holidays (non-working days highlighted on calendar).
 * Displays a list of existing holidays with inline edit/delete,
 * and a form to add new holidays.
 * 
 * @param {Object} props
 * @param {Array} props.holidays - Array of holiday objects
 * @param {Function} props.onAddHoliday - Handler for adding (title, startDate, endDate, description) => void
 * @param {Function} props.onUpdateHoliday - Handler for updating (id, updates) => void
 * @param {Function} props.onDeleteHoliday - Handler for deleting (id) => void
 * @param {Function} props.onClose - Handler for closing the modal
 */
function HolidayModal({ holidays, onAddHoliday, onUpdateHoliday, onDeleteHoliday, onClose }) {
  const { confirm: confirmDialog } = useDialog();
  const { t, formatDateRange } = useTranslation();
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);

  const handleStartAdd = () => {
    setIsAdding(true);
  };

  const handleCancelEdit = () => {
    setIsAdding(false);
    setEditingId(null);
  };

  const handleSaveNew = (formData) => {
    onAddHoliday(
      formData.title,
      formData.startDate,
      formData.endDate,
      formData.description
    );
    setIsAdding(false);
  };

  const handleSaveEdit = (id, formData) => {
    onUpdateHoliday(id, {
      title: formData.title,
      startDate: formData.startDate,
      endDate: formData.endDate,
      description: formData.description
    });
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog({
      title: t('dialog.deleteHoliday'),
      message: t('dialog.deleteHolidayConfirm'),
      confirmLabel: t('common.delete'),
      variant: 'danger'
    });
    
    if (confirmed) {
      onDeleteHoliday(id);
      if (editingId === id) {
        setEditingId(null);
      }
    }
  };

  const handleClose = () => {
    // If editing or adding, cancel that first
    if (isAdding || editingId) {
      handleCancelEdit();
    } else {
      onClose();
    }
  };

  const formatHolidayDateRange = (startDate, endDate) => {
    return formatDateRange(startDate, endDate);
  };

  // Sort holidays by date
  const sortedHolidays = [...holidays].sort((a, b) => {
    return a.startDate.localeCompare(b.startDate);
  });

  return (
    <Modal 
      isOpen 
      onClose={handleClose} 
      title={t('holiday.title')}
      maxWidth={600}
    >
      {/* Holidays list */}
      <div className="holiday-modal__list">
        {sortedHolidays.length === 0 && !isAdding && (
          <p className="holiday-modal__empty">{t('holiday.empty')}</p>
        )}
        
        {sortedHolidays.map((holiday) => (
          <div key={holiday.id} className="holiday-modal__item">
            {editingId === holiday.id ? (
              // Edit form
              <HolidayForm
                holiday={holiday}
                onSave={(formData) => handleSaveEdit(holiday.id, formData)}
                onCancel={() => setEditingId(null)}
                onDelete={handleDelete}
                isInline
              />
            ) : (
              // Display mode
              <>
                <div className="holiday-modal__item-info">
                  <div className="holiday-modal__item-title">{holiday.title}</div>
                  <div className="holiday-modal__item-date">{formatHolidayDateRange(holiday.startDate, holiday.endDate)}</div>
                </div>
                <div className="holiday-modal__item-actions">
                  <button
                    className="holiday-modal__item-btn"
                    onClick={() => setEditingId(holiday.id)}
                    title={t('holiday.edit')}
                  >
                    ✎
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Add form */}
      {isAdding ? (
        <HolidayForm
          onSave={handleSaveNew}
          onCancel={handleCancelEdit}
        />
      ) : (
        <button className="holiday-modal__add-btn" onClick={handleStartAdd}>
          {t('holiday.add')}
        </button>
      )}
    </Modal>
  );
}

export default HolidayModal;
